import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { usePoints } from '../../context/PointsContext';
import BasePageLayout from '../../components/BasePageLayout';
import '../../styles/pages/PointsHistoryPage.css';

// Enhanced type for display purposes
interface PointTransaction {
  id: string;
  points: number;
  description: string;
  created_at: string;
  expires_at?: string | null;
  is_spent?: boolean;
  source_type?: string;
  status: 'LOGIN' | 'TASK' | 'MANUAL' | 'BONUS' | 'SPENT';
}

type SortField = 'date' | 'description' | 'points' | 'status';
type SortDirection = 'asc' | 'desc';

const PointsHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { userPoints, pointsHistory, loadingHistory, historyError, fetchPointsHistory } = usePoints();
  const navigate = useNavigate();

  // State for table functionality
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  // Enhanced transaction mapping with better status classification
  const allTransactions: PointTransaction[] = useMemo(() => {
    return pointsHistory.map((item: any) => {
      let status: PointTransaction['status'] = 'BONUS';

      // Determine status based on transaction_type, source_type, description, and amount
      if (item.transaction_type === 'spent' || item.points < 0) {
        status = 'SPENT';
      } else if (item.transaction_type === 'team_request_cost' || (item.transaction_type === 'reward_debit' && item.source_type === 'team')) {
        status = 'SPENT'; // Team creation costs should show as SPENT
      } else if (item.source_type?.includes('LOGIN') || item.description?.toLowerCase().includes('login')) {
        status = 'LOGIN';
      } else if (item.source_type?.includes('TASK') || item.description?.toLowerCase().includes('task')) {
        status = 'TASK';
      } else if (item.source_type?.includes('MANUAL') || item.description?.toLowerCase().includes('manual')) {
        status = 'MANUAL';
      } else if (item.transaction_type?.includes('cost') || item.transaction_type?.includes('debit')) {
        status = 'SPENT'; // Any cost or debit transaction should show as SPENT
      }

      return {
        id: item.id,
        points: Math.abs(item.points), // Store absolute value for display
        description: item.description || `${item.source_type || 'Bonus'} points`,
        created_at: item.created_at,
        is_spent: item.points < 0 || item.transaction_type === 'spent' || item.transaction_type === 'team_request_cost' || (item.transaction_type === 'reward_debit' && item.source_type === 'team'),
        expires_at: null,
        source_type: item.source_type,
        status
      };
    });
  }, [pointsHistory]);

  // Apply filters and search
  const filteredTransactions = useMemo(() => {
    let filtered = allTransactions;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatDate(transaction.created_at).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(transaction =>
        transaction.status.toLowerCase() === filterType.toLowerCase()
      );
    }

    // Apply date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const daysAgo = parseInt(dateRange);
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));

      filtered = filtered.filter(transaction =>
        new Date(transaction.created_at) >= cutoffDate
      );
    }

    return filtered;
  }, [allTransactions, searchTerm, filterType, dateRange]);

  // Apply sorting
  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'points':
          aValue = a.points;
          bValue = b.points;
          break;
        case 'description':
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredTransactions, sortField, sortDirection]);

  // Pagination logic
  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, dateRange]);

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedTransactions, currentPage]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const earned = allTransactions
      .filter(t => !t.is_spent)
      .reduce((sum, t) => sum + t.points, 0);

    const spent = allTransactions
      .filter(t => t.is_spent)
      .reduce((sum, t) => sum + t.points, 0);

    return {
      totalEarned: earned,
      totalSpent: spent,
      netGain: earned - spent,
      hasData: allTransactions.length > 0
    };
  }, [allTransactions]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Add a retry function for the user
  const handleRetry = () => {
    fetchPointsHistory();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: PointTransaction['status']) => {
    switch (status) {
      case 'LOGIN': return 'bi-box-arrow-in-right';
      case 'TASK': return 'bi-check-circle-fill';
      case 'MANUAL': return 'bi-gear-fill';
      case 'SPENT': return 'bi-dash-circle-fill';
      default: return 'bi-gift-fill';
    }
  };

  const getStatusTooltip = (status: PointTransaction['status']) => {
    switch (status) {
      case 'LOGIN': return t('pointsHistory.loginTooltip');
      case 'TASK': return t('pointsHistory.taskTooltip');
      case 'MANUAL': return t('pointsHistory.manualTooltip');
      case 'SPENT': return t('pointsHistory.spentTooltip');
      default: return t('pointsHistory.bonusTooltip');
    }
  };

  if (loadingHistory) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="points-history-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('pointsHistory.loading')}</span>
          </div>
          <span className="ms-2">{t('pointsHistory.loading')}</span>
        </div>
      </BasePageLayout>
    );
  }

  if (historyError) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {historyError}
          <button onClick={handleRetry} className="btn btn-outline-danger btn-sm ms-3">
            {t('pointsHistory.retry')}
          </button>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="points-history-page">
        {/* Header Section - Similar to ClaimBonusesPage */}
        <section className="points-header-section">
          <div className="points-header-content">
            <div className="points-hero-centered">
              <div className="points-icon-wrapper">
                <div className="points-icon-large">
                  <span className="points-icon-placeholder">🪙</span>
                </div>
              </div>

              <div className="points-info">
                <div className="points-title-group">
                  <h1 className="points-title-main">{t('pointsHistory.title')}</h1>
                  <div className="points-badge">
                    <i className="bi bi-clock-history"></i>
                    {t('pointsHistory.transactionLog')}
                  </div>
                </div>
                <p className="points-description">
                  {t('pointsHistory.description')}
                </p>

                <div className="points-meta">
                  <div className="meta-item">
                    <i className="bi bi-wallet2"></i>
                    <span>{t('pointsHistory.currentBalance', { count: userPoints.toLocaleString() })}</span>
                  </div>
                  <div className="meta-item">
                    <i className="bi bi-list"></i>
                    <span>{t('pointsHistory.totalTransactions', { count: allTransactions.length })}</span>
                  </div>
                  {statistics.hasData && (
                    <div className="meta-item">
                      <i className="bi bi-graph-up"></i>
                      <span>{t('pointsHistory.net', { amount: (statistics.netGain >= 0 ? '+' : '') + statistics.netGain.toLocaleString() })}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Container with ClaimBonusesPage-style Background */}
        <section className="points-wide-container">
          <div className="points-main-content">
            {/* Statistics Section */}
            <div className="points-statistics-section">
              <h3 className="section-title">
                <i className="bi bi-bar-chart"></i>
                {t('pointsHistory.statisticsOverview')}
              </h3>
              <div className="statistics-grid">
                <div className="stat-card">
                  <div className="stat-icon positive">
                    <i className="bi bi-arrow-up-circle"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value positive">
                      +{statistics.totalEarned.toLocaleString()}
                    </div>
                    <div className="stat-label">{t('pointsHistory.totalEarned')}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon negative">
                    <i className="bi bi-arrow-down-circle"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value negative">
                      -{statistics.totalSpent.toLocaleString()}
                    </div>
                    <div className="stat-label">{t('pointsHistory.totalSpent')}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className={`stat-icon ${statistics.netGain >= 0 ? 'positive' : 'negative'}`}>
                    <i className="bi bi-graph-up"></i>
                  </div>
                  <div className="stat-content">
                    <div className={`stat-value ${statistics.netGain >= 0 ? 'positive' : 'negative'}`}>
                      {statistics.netGain >= 0 ? '+' : ''}{statistics.netGain.toLocaleString()}
                    </div>
                    <div className="stat-label">{t('pointsHistory.netGain')}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon neutral">
                    <i className="bi bi-list-ul"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value neutral">
                      {allTransactions.length.toLocaleString()}
                    </div>
                    <div className="stat-label">{t('pointsHistory.totalTransactions', { count: '' })}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search Section */}
            <div className="points-filters-section">
              <h3 className="section-title">
                <i className="bi bi-funnel"></i>
                {t('pointsHistory.filterAndSearch')}
              </h3>
              <div className="filters-container">
                <div className="search-bar">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder={t('pointsHistory.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>

                <div className="filter-controls">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">{t('pointsHistory.allTypes')}</option>
                    <option value="login">{t('pointsHistory.login')}</option>
                    <option value="task">{t('pointsHistory.task')}</option>
                    <option value="manual">{t('pointsHistory.manual')}</option>
                    <option value="bonus">{t('pointsHistory.bonus')}</option>
                    <option value="spent">{t('pointsHistory.spent')}</option>
                  </select>

                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">{t('pointsHistory.allTime')}</option>
                    <option value="7">{t('pointsHistory.last7Days')}</option>
                    <option value="30">{t('pointsHistory.last30Days')}</option>
                    <option value="90">{t('pointsHistory.last90Days')}</option>
                    <option value="365">{t('pointsHistory.lastYear')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="transactions-section">
              <div className="transactions-container">
                <div className="transactions-header">
                  <h3 className="section-title">
                    <i className="bi bi-table"></i>
                    {t('pointsHistory.transactionHistory')}
                  </h3>
                  <span className="transaction-count">
                    {t('pointsHistory.transactionsCount', { count: filteredTransactions.length, total: allTransactions.length })}
                  </span>
                </div>

                {filteredTransactions.length === 0 ? (
                  <div className="empty-state">
                    <i className="bi bi-inbox empty-icon"></i>
                    <h4>{t('pointsHistory.noTransactionsFound')}</h4>
                    <p>{t('pointsHistory.noTransactionsDesc')}</p>
                  </div>
                ) : (
                  <div className="transactions-table-wrapper">
                    <table className="transactions-table">
                      <thead>
                        <tr>
                          <th
                            onClick={() => handleSort('date')}
                            className={`sortable ${sortField === 'date' ? 'active' : ''}`}
                          >
                            {t('pointsHistory.date')}
                            {sortField === 'date' && (
                              <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th
                            onClick={() => handleSort('description')}
                            className={`sortable ${sortField === 'description' ? 'active' : ''}`}
                          >
                            {t('pointsHistory.description')}
                            {sortField === 'description' && (
                              <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th
                            onClick={() => handleSort('status')}
                            className={`sortable ${sortField === 'status' ? 'active' : ''}`}
                          >
                            {t('pointsHistory.type')}
                            {sortField === 'status' && (
                              <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th
                            onClick={() => handleSort('points')}
                            className={`sortable ${sortField === 'points' ? 'active' : ''}`}
                          >
                            {t('pointsHistory.points')}
                            {sortField === 'points' && (
                              <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTransactions.map((transaction) => (
                          <tr key={transaction.id} className="transaction-row">
                            <td className="date-cell">
                              {formatDate(transaction.created_at)}
                            </td>
                            <td className="description-cell">
                              {transaction.description}
                            </td>
                            <td className="status-cell">
                              <span
                                className={`status-badge ${transaction.status.toLowerCase()}`}
                                title={getStatusTooltip(transaction.status)}
                              >
                                <i className={getStatusIcon(transaction.status)}></i>
                                {t(`pointsHistory.status${transaction.status.charAt(0) + transaction.status.slice(1).toLowerCase()}`)}
                              </span>
                            </td>
                            <td className="points-cell">
                              <span className={`points-value ${transaction.is_spent ? 'negative' : 'positive'}`}>
                                {transaction.is_spent ? '-' : '+'}{Math.abs(transaction.points).toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                      {t('pointsHistory.previousPage')}
                    </button>

                    <span className="pagination-info">
                      {t('pointsHistory.pageInfo', { current: currentPage, total: totalPages })}
                    </span>

                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      {t('pointsHistory.nextPage')}
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </BasePageLayout>
  );
};

export default PointsHistoryPage;
